// Port of ios/Sources/Session/ScreenAwake.swift (Kotlin twin `system/ScreenAwake.kt`).
//
// WHO IS HOLDING THE DISPLAY AWAKE — and the only thing allowed to answer that question.
//
// The browser's Screen Wake Lock is one switch for the whole page, exactly like Android's
// `FLAG_KEEP_SCREEN_ON` belongs to the window: whatever takes it holds the display for the WHOLE APP
// until something releases it. The web had three writers (the call, the voice recorder, and — the
// gap PARITY P304 recorded — nothing for the remote-support viewer), each with its own sentinel, so a
// second holder could not know the screen was already held and the first one's release dropped it.
//
// Holds are KEYED here and counted; the switch is written in ONE place (`apply`, given by the host:
// ui/screenAwake.ts requests / releases `navigator.wakeLock`). Pure: no DOM, no timers.

export class ScreenAwake {
  /** The one call this tab is on — expanded, or collapsed to the bar. */
  static readonly call = 'call';
  /** The remote-support viewer while it is open (the helper watches a live screen). */
  static readonly remoteSupport = 'remote-support';

  private static voiceNoteIds = 0;

  /**
   * A key for ONE composer's take. A conversation and its thread pane are two composers on screen
   * at once, and each must release its OWN hold without touching the other's.
   */
  static nextVoiceNoteKey(): string {
    ScreenAwake.voiceNoteIds += 1;
    return `voice-note:${ScreenAwake.voiceNoteIds}`;
  }

  private holderSet = new Set<string>();

  /** `apply(held)` runs only when the answer to "is anything holding the screen?" CHANGES. */
  constructor(private readonly apply: (held: boolean) => void = () => undefined) {}

  /** Every reason the display is being held awake right now. Empty = the screen may sleep. */
  get holders(): ReadonlySet<string> {
    return this.holderSet;
  }

  get isHeld(): boolean {
    return this.holderSet.size > 0;
  }

  /** Takes or drops `key`'s hold. A key is a set member, so taking the same one twice is one hold. */
  set(key: string, held: boolean): void {
    const before = this.holderSet.size > 0;
    if (held) this.holderSet.add(key);
    else this.holderSet.delete(key);
    const after = this.holderSet.size > 0;
    if (before !== after) this.apply(after);
  }

  hold(key: string): void {
    this.set(key, true);
  }

  release(key: string): void {
    this.set(key, false);
  }

  /** Gives the display back unconditionally — the app is leaving, or a test is starting. */
  clear(): void {
    const before = this.holderSet.size > 0;
    this.holderSet = new Set();
    if (before) this.apply(false);
  }
}
